import pc from "picocolors";

export type OutputMode = "pretty" | "json" | "auto";
export type ResolvedOutputMode = "pretty" | "json";

export interface Column<T> {
  key: keyof T & string;
  label: string;
  format?: (value: T[keyof T & string], row: T) => string;
}

/**
 * Resolve a user-facing output mode. `auto` checks whether stdout is a TTY:
 * TTY → pretty, pipe/redirect → json. Mirrors gh, kubectl, az.
 */
export function resolveOutputMode(
  flag: OutputMode | undefined,
  stream: NodeJS.WriteStream = process.stdout,
): ResolvedOutputMode {
  if (flag === "pretty") return "pretty";
  if (flag === "json") return "json";
  return stream.isTTY ? "pretty" : "json";
}

/**
 * Render an array of records as a padded table. Single-line cells only —
 * multi-line values get joined with spaces.
 */
export function renderTable<T extends Record<string, unknown>>(
  rows: T[],
  columns: Column<T>[],
): string {
  if (rows.length === 0) return "(no rows)";

  const headers = columns.map((c) => c.label);
  const dataRows = rows.map((row) =>
    columns.map((c) => {
      const v = row[c.key];
      if (c.format) return c.format(v, row);
      if (v === undefined || v === null || v === "") return "-";
      return String(v).replace(/\s+/g, " ");
    }),
  );

  const widths = columns.map((_, i) => {
    const headerWidth = headers[i].length;
    const maxData = dataRows.reduce((m, r) => Math.max(m, r[i].length), 0);
    return Math.max(headerWidth, maxData);
  });

  const headerLine = headers.map((h, i) => h.padEnd(widths[i])).join("  ");
  const dataLines = dataRows.map((r) =>
    r.map((cell, i) => cell.padEnd(widths[i])).join("  ").trimEnd(),
  );

  return [headerLine.trimEnd(), ...dataLines].join("\n");
}

/**
 * Render a single object as an aligned key-value block.
 */
export function renderKeyValueBlock(
  rows: { key: string; value: string | undefined }[],
): string {
  if (rows.length === 0) return "";
  const keyWidth = rows.reduce((m, r) => Math.max(m, r.key.length), 0);
  return rows
    .map((r) => `${r.key.padEnd(keyWidth)}:  ${r.value ?? "-"}`)
    .join("\n");
}

export function renderJson(data: unknown): string {
  return JSON.stringify(data, null, 2);
}

/**
 * Color helpers — picocolors auto-strips on non-TTY and honors NO_COLOR.
 */
export const colors = {
  error: (s: string) => pc.red(pc.bold(s)),
  hint: (s: string) => pc.dim(s),
  success: (s: string) => pc.green(s),
  field: (s: string) => pc.bold(s),
  dim: (s: string) => pc.dim(s),
};
